import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get, post } from "../../../http/api";
import { useTranslation } from "react-i18next";

const SubscriptionSection = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadBilling = async () => {
      try {
        const response = await get("billing/status");

        if (!cancelled) {
          setBilling(response.data);
          setError(false);
        }
      } catch (err) {
        console.error("Billing status error");

        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadBilling();

    return () => {
      cancelled = true;
    };
  }, []);

  const openBilling = () => {
    navigate("/billing");
  };

  const manageSubscription = async () => {
    try {
      setPortalLoading(true);

      const response = await post("billing/portal", {});

      if (!response.data?.url) {
        throw new Error("Portal unavailable");
      }

      const url = new URL(response.data.url);

      if (
        url.protocol !== "https:" ||
        url.hostname !== "billing.stripe.com"
      ) {
        throw new Error("Invalid billing portal URL");
      }

      window.location.assign(url.href);
    } catch (err) {
      console.error("Unable to open billing portal");
      setPortalLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return null;

    const localeMap = {
      fr: "fr-FR",
      en: "en-GB",
      es: "es-ES",
      ar: "ar-MA"
    };

    const language = (
      i18n.resolvedLanguage ||
      i18n.language ||
      "fr"
    ).split("-")[0];

    return new Intl.DateTimeFormat(
      localeMap[language] || "fr-FR",
      {
        day: "numeric",
        month: "long",
        year: "numeric"
      }
    ).format(new Date(date));
  };

  if (loading) {
    return (
      <section className="subscription-settings-card">
        <div className="subscription-settings-loading">
          {t("subscription.loading")}
        </div>
      </section>
    );
  }

  if (error || !billing) {
    return (
      <section className="subscription-settings-card">
        <h3>{t("subscription.title")}</h3>

        <p className="subscription-settings-error">
          {t("subscription.error")}
        </p>
      </section>
    );
  }

  const isPremium = billing.premium === true;

  const getPlanName = () => {
    if (billing.lifetime) {
      return t("subscription.plans.lifetime");
    }

    if (billing.plan === "yearly") {
      return t("subscription.plans.yearly");
    }

    if (billing.plan === "monthly") {
      return t("subscription.plans.monthly");
    }

    return t("subscription.plans.premium");
  };

  return (
    <section
      className={`settings-card subscription-card ${
        isPremium ? "is-premium" : "is-free"
      }`}
    >
      <div className="subscription-settings-top">
        <div>
          <span className="subscription-settings-kicker">
            {t("subscription.kicker")}
          </span>

          <h3>
            {isPremium
              ? t("subscription.premiumName")
              : t("subscription.freeName")}
          </h3>

          <p>
            {isPremium
              ? t("subscription.premiumDescription")
              : t("subscription.freeDescription")}
          </p>
        </div>

        <span
          className={`subscription-status-badge ${
            isPremium ? "premium" : "free"
          }`}
        >
          {isPremium
            ? t("subscription.badges.premium")
            : t("subscription.badges.free")}
        </span>
      </div>

      {isPremium ? (
        <div className="subscription-details">

          <div className="subscription-detail-row">
            <span>{t("subscription.labels.plan")}</span>
            <strong>{getPlanName()}</strong>
          </div>

          {!billing.lifetime && billing.paidUntil && (
            <div className="subscription-detail-row">
              <span>
                {billing.cancelAtPeriodEnd
                  ? t("subscription.labels.accessUntil")
                  : t("subscription.labels.nextBilling")}
              </span>

              <strong>
                {formatDate(billing.paidUntil)}
              </strong>
            </div>
          )}

          {!billing.lifetime && (
            <div className="subscription-detail-row">
              <span>
                {t("subscription.labels.renewal")}
              </span>

              <strong>
                {billing.cancelAtPeriodEnd
                  ? t("subscription.renewal.cancelled")
                  : t("subscription.renewal.automatic")}
              </strong>
            </div>
          )}

          {billing.cancelAtPeriodEnd && (
            <div className="subscription-ending-message">
              {t("subscription.ending", {
                date: formatDate(billing.paidUntil)
              })}
            </div>
          )}

          {billing.canManage && !billing.lifetime && (
            <button
              className="subscription-manage-btn"
              onClick={manageSubscription}
              disabled={portalLoading}
            >
              {portalLoading
                ? t("subscription.buttons.opening")
                : t("subscription.buttons.manage")}
            </button>
          )}
        </div>
      ) : (
        <div className="subscription-free-content">

          <div className="subscription-benefits">
            <span>
              ✓ {t("subscription.benefits.profileVisits")}
            </span>

            <span>
              ✓ {t("subscription.benefits.advancedSearch")}
            </span>

            <span>
              ✓ {t("subscription.benefits.matching")}
            </span>
          </div>

          <button
            className="subscription-upgrade-btn"
            onClick={openBilling}
          >
            {t("subscription.buttons.discover")}
          </button>
        </div>
      )}
    </section>
  );
};

export default SubscriptionSection;